/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { API } from 'aws-amplify';

import { customersList } from '../../Jobs/Components/AddEditOneTimeJob/constants';
import { ICustomAPIEndpoint, IGraphQLError, IGraphQLResponse } from '../../../Shared/Models';

import {
  BooleanType,
  CreateFacilityAccessDetails,
  FilterCustomerInput,
  UpdateFacilityAccessDetails,
} from '../../../API';
import awsConfig from '../../../Configuration/environment-config';
import { addCustomerFacilityAccess, updateCustomerFacilityAccess, processBidReview } from '../../../graphql/mutations';
import {
  bidProposalSummary,
  customer,
  customerAddresses,
  customerDetails,
  customerFacilityAccess,
  emailTransaction,
  getAllUsers,
  getAssociateDetailsByUserId,
  getCustomers,
  getUserManagers,
} from '../../../graphql/queries';
import { AuthHeader, AWSAuthMode } from '../../../Shared/Constants/App';
import { AuthenticationService } from '../../../Shared/Services/AuthenticationService';
import { getWebInfo, signedFetch } from '../../../Shared/Utilities/utils';
import { EmailTransactionDetailsModel, IPOAttachments } from '../Models/BidApproval.Model';

type FetchTermsAndConditionsResponse = {
  pdfUrl: string;
};

export default class CustomerApiService {
  static async getFacilityAccess(customerId: string, facilityId: string) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const facilityAccessResponse: IGraphQLResponse = await API.graphql(
        {
          query: customerFacilityAccess,
          variables: { customerId, facilityId },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );

      return { data: facilityAccessResponse?.data?.customerFacilityAccess ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async verifyTin(tin: string, name: string) {
    try {
      const verifyTinEndpoint = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint) => apiEndpoint.name === 'verify-tin'
      ) as ICustomAPIEndpoint | undefined;
      if (verifyTinEndpoint?.endpoint) {
        const tinStatus = await fetch(verifyTinEndpoint?.endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-PZY-Client-Type': 'Web' },
          body: JSON.stringify({ requestType: 'validateTin', tin, name }),
        });
        const responseJson = await tinStatus.json();
        return { data: responseJson ?? [], errors: [] };
      }
      return { data: [], errors: [{ message: 'Endpoint not found' }] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getCustomer(id: string) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const customerResponse: IGraphQLResponse = await API.graphql(
        {
          query: customer,
          variables: { id },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: customerResponse.data?.customer ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getCustomers(filterCustomerInput: FilterCustomerInput) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const customerResponse: IGraphQLResponse = await API.graphql(
        {
          query: getCustomers,
          variables: { filterCustomerInput },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: customerResponse.data?.getCustomers ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getCustomerAddress(id: string) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const customerResponse: IGraphQLResponse = await API.graphql(
        {
          query: customerAddresses,
          variables: { customerId: id },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: customerResponse.data?.customerAddresses ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static getCustomersList(isMocked = true) {
    if (isMocked) {
      return customersList;
    }
  }

  static async getCustomerDetails(id: string) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const customerDetailsResponse: IGraphQLResponse = await API.graphql(
        {
          query: customerDetails,
          variables: { id },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: customerDetailsResponse.data ?? [], errors: [] };
    } catch (e: any) {
      return { data: null, errors: e.errors };
    }
  }

  static async getCustomerDetailsResponse(id: string) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const customerResponse: IGraphQLResponse = await API.graphql(
        {
          query: customer,
          variables: { id },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: customerResponse.data ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getBidProposalSummary(bidId: string) {
    try {
      const bidProposalSummaryResponse: IGraphQLResponse = await API.graphql(
        {
          query: bidProposalSummary,
          variables: { bidId },
          authMode: AWSAuthMode.API_KEY,
        },
        { ...getWebInfo() }
      );
      return { data: bidProposalSummaryResponse.data.bidProposalSummary ?? [], errors: [] };
    } catch (e: any) {
      return { data: null, errors: e.errors };
    }
  }

  static async acceptRejectBidProposal(
    transactionId: string,
    bidStatus: string,
    options: {
      userId?: string;
      ipAddress?: string;
      comments?: string;
      isFromEmail?: boolean;
      jobId?: string;
      bidId?: string;
      attachments?: IPOAttachments[];
      specialInstructions?: string;
    } = {}
  ) {
    const { ipAddress, comments, isFromEmail = false, jobId, bidId, attachments, specialInstructions } = options;
    const processBidReviewInput: Record<string, unknown> = {};
    if (comments) processBidReviewInput.comments = comments;
    if (ipAddress) processBidReviewInput.IPAddress = ipAddress;
    processBidReviewInput.isFromEmail = isFromEmail;
    if (jobId) processBidReviewInput.jobId = jobId;
    if (bidId) processBidReviewInput.bidId = bidId;
    if (attachments)
      processBidReviewInput.attachments = attachments.map(({ id, ...rest }) => ({
        ...rest,
        attachmentId: id,
        fileHash: '',
      }));
    if (specialInstructions) processBidReviewInput.specialInstructions = specialInstructions;
    try {
      const processBidReviewResponse: IGraphQLResponse = await API.graphql(
        {
          query: processBidReview,
          variables: {
            transactionId: transactionId || '123',
            bidStatus,
            processBidReviewInput,
          },
          authMode: AWSAuthMode.API_KEY,
        },
        { ...getWebInfo() }
      );
      return { data: processBidReviewResponse.data.processBidReview ?? [], errors: [] };
    } catch (e: any) {
      return { data: null, errors: e.errors };
    }
  }

  static async fetchTermsAndConditions(): Promise<FetchTermsAndConditionsResponse> {
    const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'getTermsAndConditions');

    if (!endpointConfig?.endpoint) {
      throw new Error('Endpoint configuration not found');
    }

    // Plain, header-less GET so the browser treats it as a "simple" CORS request and skips the
    // preflight (custom headers like X-PZY-Client-Type/Content-Type would otherwise be rejected by
    // the preflight response). No auth — works in the unauthenticated bid-approval email flow;
    // requires the API Gateway route for /jobs/job-bids/velociti-tc-pdf to be public.
    const response = await fetch(endpointConfig.endpoint, { method: 'GET' });

    const responseJson = await response.json();
    const pdfArray = responseJson?.data?.data;
    if (!pdfArray) {
      throw new Error('Failed to fetch terms and conditions');
    }

    const uint8Array = new Uint8Array(pdfArray);

    const blob = new Blob([uint8Array], { type: 'application/pdf' });

    const pdfUrl = URL.createObjectURL(blob);

    return { pdfUrl };
  }

  static async getEmailTransactionDetails(
    emailTransactionId: string
  ): Promise<{ data: EmailTransactionDetailsModel; errors: [] } | { data: []; errors: [] }> {
    try {
      const emailTransactionResponse: IGraphQLResponse = await API.graphql(
        {
          query: emailTransaction,
          variables: { emailTransactionId },
          authMode: AWSAuthMode.API_KEY,
        },
        { ...getWebInfo() }
      );
      return { data: emailTransactionResponse?.data?.emailTransaction ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getUserManagers(
    managerId: string,
    userId?: string,
    isAssociate?: BooleanType,
    searchString?: string,
    pageNumber?: number,
    limit?: number,
    isPaginationRequire = false // This flag can use if pagination is required for this query.
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const variables: {
        isAssociate?: string;
        limit?: number;
        managerId: string;
        pageNumber?: number;
        searchString?: string;
        userId?: string;
      } = {
        managerId,
      };
      if (!isEmpty(userId)) {
        variables.userId = userId;
      }
      if (!isEmpty(isAssociate)) {
        variables.isAssociate = isAssociate;
      }
      if (!isEmpty(searchString)) {
        variables.searchString = searchString;
      }
      if (pageNumber && pageNumber > 0) {
        variables.pageNumber = pageNumber;
      }
      if (limit && limit > 0) {
        variables.limit = limit;
      }
      if (managerId) {
        let userManagersResponse: IGraphQLResponse = await API.graphql(
          {
            query: getUserManagers,
            variables,
            authMode: AWSAuthMode.COGNITO,
          },
          { ...getWebInfo(), ...authHeaders }
        );
        if (!isPaginationRequire) {
          userManagersResponse = await API.graphql(
            {
              query: getUserManagers,
              variables: {
                ...variables,
                limit: userManagersResponse?.data?.getUserManagers?.metadata?.totalCount ?? 20,
              },
              authMode: AWSAuthMode.COGNITO,
            },
            { ...getWebInfo(), ...authHeaders }
          );
        }
        return {
          data: userManagersResponse?.data?.getUserManagers?.data ?? [],
          metadata: userManagersResponse?.data?.getUserManagers?.metadata,
          errors: [],
        };
      }
      return { data: [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getAllUsers(
    organizationId: string,
    roleName?: string,
    pageNumber?: number,
    limit?: number,
    isAssociate?: BooleanType,
    isPaginationRequire = false, // This flag can use if pagination is required for this query.
    searchString?: string
  ) {
    try {
      const variables: {
        isAssociate?: BooleanType;
        limit?: number;
        organizationId: string;
        pageNumber?: number;
        roleName?: string;
        searchString?: string;
      } = {
        organizationId: '',
        roleName: '',
        limit: 10,
        pageNumber: 1,
      };
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      if (organizationId) {
        variables.organizationId = organizationId;
      }
      if (roleName) {
        variables.roleName = roleName;
      }
      if (limit) {
        variables.limit = limit;
      }
      if (pageNumber && pageNumber > 0) {
        variables.pageNumber = pageNumber;
      }
      if (limit && limit > 0) {
        variables.limit = limit;
      }
      if (isAssociate) {
        variables.isAssociate = isAssociate;
      }
      if (searchString) {
        variables.searchString = searchString;
      }

      let usersResponse: IGraphQLResponse = await API.graphql(
        {
          query: getAllUsers,
          variables: { ...variables },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );

      if (!isPaginationRequire) {
        usersResponse = await API.graphql(
          {
            query: getAllUsers,
            variables: {
              ...variables,
              limit: usersResponse?.data?.getAllUsers?.metadata?.totalCount ?? 20,
            },
            authMode: AWSAuthMode.COGNITO,
          },
          { ...getWebInfo(), ...authHeaders }
        );
      }
      return {
        data: usersResponse?.data?.getAllUsers?.data ?? [],
        metadata: usersResponse?.data?.getAllUsers?.metadata ?? {},
        errors: [],
      };
    } catch (e: any) {
      return { data: [], errors: e?.errors ?? [], metadata: {} };
    }
  }

  static async getAssociateDetailsByUserId(cognitoUserId: string) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const associateDetailsByUserIdResponse: IGraphQLResponse = await API.graphql(
        {
          query: getAssociateDetailsByUserId,
          variables: { cognitoUserId },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: associateDetailsByUserIdResponse?.data.getAssociateDetailsByUserId ?? {}, errors: [] };
    } catch (e: unknown) {
      const apiError: IGraphQLError = e as IGraphQLError;
      return { data: {}, errors: apiError?.errors ?? [] };
    }
  }

  static async saveFacilityAccessDetails(
    customerId: string,
    facilityId: string,
    createCustomerFacilityAccessInput: CreateFacilityAccessDetails
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const facilityAccessResponse: IGraphQLResponse = await API.graphql(
        {
          query: addCustomerFacilityAccess,
          variables: { customerId, facilityId, createCustomerFacilityAccessInput },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: facilityAccessResponse?.data?.addCustomerFacilityAccess ?? null, errors: [] };
    } catch (e: any) {
      return { data: null, errors: e.errors };
    }
  }
  static async updateFacilityAccess(
    customerId: string,
    facilityId: string,
    updateCustomerFacilityAccessInput: UpdateFacilityAccessDetails
  ) {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const facilityAccessResponse: IGraphQLResponse = await API.graphql(
        {
          query: updateCustomerFacilityAccess,
          variables: { customerId, facilityId, updateCustomerFacilityAccessInput },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: facilityAccessResponse?.data?.updateCustomerFacilityAccess ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async fetchProcessEvents(userId: string, orgId: string, processId?: number) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'getProcessEvents');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const finalEndpoint = endpointConfig?.endpoint?.replace(':processId', `${processId}`);
      const url = new URL(finalEndpoint);

      const response = await signedFetch({
        method: 'GET',
        url: url.toString(),
        requestId: uuidv4(),
        abnHeaders: {
          userid: userId,
          orgid: orgId,
          devicetype: 'D',
          userlanguage: 'en',
        },
      });

      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (e: any) {
      console.error(`Error occured while fetching events`, e);
    }
  }
}
